import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PATIENTS, METRICS } from './data';
import './App.css';

const OPENAI_KEY = process.env.REACT_APP_OPENAI_API_KEY ?? '';

/* ── helpers ─────────────────────────────── */
const riskOf = p =>
  p >= 0.70 ? 'red' : p >= 0.40 ? 'orange' : 'green';

const riskLabel = p =>
  p >= 0.70 ? 'ALT' : p >= 0.40 ? 'MODERAT' : 'BAIX';

const pct = p => p != null ? `${(p * 100).toFixed(1)}%` : '—';

const CRONIC_MAP = {
  NO:   'Sense patologia crònica complexa',
  PCC:  'Pacient Crònic Complex (PCC)',
  MACA: 'Malaltia Crònica Avançada (MACA)',
};

/* ── Prompt builder ──────────────────────────────────── */
function buildPrompt(p, combined) {
  const shapLines = [...p.shap]
    .sort((a, b) => Math.abs(b.shap) - Math.abs(a.shap))
    .slice(0, 6)
    .map(f => `  • ${f.name} = ${f.value}  [${f.shap > 0 ? '↑ incrementa' : '↓ redueix'} risc, SHAP=${f.shap > 0 ? '+' : ''}${f.shap.toFixed(3)}]`)
    .join('\n');

  const system = `Eres un médico internista experto en pacientes crónicos complejos y medicina de precisión.
Tu tarea es redactar el resumen clínico de un paciente a partir de datos estructurados extraídos de su historial electrónico y de los resultados de modelos de IA clínica.

Reglas estrictas:
- Redacta exclusivamente en español formal médico.
- Sé clínico, denso y preciso. Sin frases vacías ni repetición de datos en bruto.
- Integra las predicciones de los modelos como información clínica adicional, no como nota aislada.
- El tono es el de una nota de interconsulta de alta calidad entre especialistas.
- Longitud objetivo: entre 180 y 260 palabras. Ni más ni menos.
- Formato de salida: un único bloque de texto continuo, sin headers, sin bullet points, sin markdown.`;

  const user = `Genera el resumen clínico del siguiente paciente.

PERFIL
  ID: ${p.id} · Sexo: ${p.sexe === 'H' ? 'Hombre' : 'Mujer'} · Grupo de edad: ${p.grup_edat} años
  Perfil de cronicidad: ${CRONIC_MAP[p.cronic] ?? p.cronic}

PREDICCIONES DE MODELOS DE IA
  Probabilidad de mortalidad a 12 meses:          ${pct(p.prob_mort)}
  Probabilidad de visita a urgencias próximo mes: ${pct(p.prob_urgencia)}
  Score integrado (0.7·mort + 0.3·urg):           ${pct(combined)}

ACTIVIDAD CLÍNICA
  Diagnósticos totales: ${p.diags} · Fármacos activos: ${p.farmacs}
  Urgencias (año): ${p.urgencies} · Ingresos (año): ${p.ingressos}
  ${p.nearest_pcc ? `Pacientes PCC similares (KNN): #${p.nearest_pcc.join(', #')}` : ''}

FACTORES DETERMINANTES SHAP (explicabilidad del modelo de mortalidad)
${shapLines}

Redacta ahora el resumen clínico. Texto continuo, sin listas ni títulos, entre 180 y 260 palabras, en español médico formal. Interpreta los datos, no los repitas en bruto.`;

  return { system, user };
}

/* ── Descàrrega PDF via finestra d'impressió ─────────── */
function descarregarPdf(p, combined, text, timestamp) {
  const riskColor = { red: '#c0392b', orange: '#e67e22', green: '#27ae60' };
  const rc = (v) => v >= 0.70 ? 'red' : v >= 0.40 ? 'orange' : 'green';

  const win = window.open('', '_blank', 'width=860,height=1000');
  win.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Informe Pacient #${p.id}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@300;400;600;700&family=Source+Serif+4:ital,wght@0,400;0,600;1,400&family=Source+Code+Pro:wght@400;600&display=swap');

    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Source Sans 3', sans-serif;
      color: #1a2e44;
      background: #fff;
      font-size: 13px;
      line-height: 1.5;
      padding: 48px 56px;
      max-width: 760px;
      margin: 0 auto;
    }

    /* Capçalera */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding-bottom: 16px;
      margin-bottom: 24px;
      border-bottom: 3px solid #0071bc;
    }
    .header-brand { display: flex; align-items: center; gap: 12px; }
    .header-cross {
      width: 36px; height: 36px;
      background: #0071bc; border-radius: 4px;
      display: flex; align-items: center; justify-content: center;
      color: #fff; font-size: 18px; font-weight: 700;
    }
    .header-name { font-size: 14px; font-weight: 700; color: #0071bc; }
    .header-sub  { font-size: 10px; color: #6b7f96; margin-top: 2px; }
    .header-meta { text-align: right; font-family: 'Source Code Pro', monospace; font-size: 10px; color: #6b7f96; }

    /* Títol */
    h1 { font-family: 'Source Serif 4', serif; font-size: 20px; font-weight: 600; margin-bottom: 4px; }
    .subtitle { font-size: 12px; color: #6b7f96; margin-bottom: 20px; }

    /* Pills de dades */
    .pills { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px; }
    .pill {
      font-size: 11px; font-weight: 600;
      padding: 4px 12px; border-radius: 20px;
      border: 1px solid #d8e2ed; background: #f7f9fb;
    }
    .pill span { color: #6b7f96; font-weight: 400; margin-right: 3px; }

    /* Prediccions */
    .scores { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 24px; }
    .score-card {
      border: 1px solid #d8e2ed; border-radius: 6px;
      padding: 14px 16px;
    }
    .score-label {
      font-family: 'Source Code Pro', monospace;
      font-size: 9px; letter-spacing: 0.1em;
      text-transform: uppercase; color: #6b7f96; margin-bottom: 6px;
    }
    .score-num {
      font-family: 'Source Serif 4', serif;
      font-size: 32px; font-weight: 600; line-height: 1; margin-bottom: 4px;
    }
    .score-sub { font-size: 10px; color: #6b7f96; }
    .bar { height: 3px; background: #eef2f6; border-radius: 2px; margin-top: 8px; }
    .bar-fill { height: 100%; border-radius: 2px; }

    /* SHAP */
    .shap-section { margin-bottom: 24px; }
    .shap-row {
      display: grid; grid-template-columns: 160px 40px 1fr 60px;
      gap: 10px; align-items: center;
      padding: 5px 0; border-bottom: 1px solid #eef2f6; font-size: 11px;
    }
    .shap-row:last-child { border-bottom: none; }
    .shap-name { color: #1a2e44; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .shap-val  { text-align: right; color: #6b7f96; font-weight: 500; }
    .shap-track { height: 6px; background: #eef2f6; border-radius: 3px; overflow: hidden; }
    .shap-fill-pos { height: 100%; background: #c0392b; border-radius: 3px; }
    .shap-fill-neg { height: 100%; background: #27ae60; border-radius: 3px; }
    .shap-delta { text-align: right; font-weight: 700; font-size: 11px; }

    /* Text de l'informe */
    .section-label {
      font-family: 'Source Code Pro', monospace;
      font-size: 9px; font-weight: 700; letter-spacing: 0.12em;
      text-transform: uppercase; color: #0071bc;
      margin-bottom: 10px; padding-bottom: 6px;
      border-bottom: 2px solid #e8f3fb;
    }
    .report-text {
      font-family: 'Source Serif 4', serif;
      font-size: 14px; line-height: 1.9;
      color: #1a2e44; text-align: justify;
    }

    /* Peu */
    .footer {
      margin-top: 32px; padding-top: 14px;
      border-top: 1px solid #d8e2ed;
      font-family: 'Source Code Pro', monospace;
      font-size: 9px; color: #b0bec8;
      display: flex; justify-content: space-between;
    }
    .disclaimer {
      font-size: 10px; color: #6b7f96;
      background: #e8f3fb; border-radius: 4px;
      padding: 8px 12px; margin-top: 14px; line-height: 1.5;
    }

    @media print {
      body { padding: 0; }
      @page { margin: 20mm 18mm; size: A4; }
    }
  </style>
</head>
<body>

  <div class="header">
    <div class="header-brand">
      <div class="header-cross">+</div>
      <div>
        <div class="header-name">Hospital Joan XXIII · ICS</div>
        <div class="header-sub">Sistema integrat d'alerta clínica · GiPSS · Hackató URV 2025</div>
      </div>
    </div>
    <div class="header-meta">
      Generat: ${timestamp}<br>
      Model: GPT-4o · OpenAI
    </div>
  </div>

  <h1>Resum Clínic · Pacient #${p.id}</h1>
  <div class="subtitle">Informe generat automàticament pel sistema de suport clínic amb IA</div>

  <div class="pills">
    <div class="pill"><span>Edat:</span>${p.grup_edat} anys</div>
    <div class="pill"><span>Sexe:</span>${p.sexe === 'H' ? 'Home' : 'Dona'}</div>
    <div class="pill" style="background:#e8f3fb;border-color:#cce2f5">
      <span>Crònic:</span>${CRONIC_MAP[p.cronic] ?? p.cronic}
    </div>
  </div>

  <div class="scores">
    ${[
      { label: 'Mortalitat · 12 mesos', val: p.prob_mort,    sub: 'Model de supervivència' },
      { label: 'Urgències · proper mes', val: p.prob_urgencia, sub: 'Model de reingrés' },
      { label: 'Score integrat',         val: combined,        sub: '0.7·mort + 0.3·urg' },
    ].map(({ label, val, sub }) => {
      const col = riskColor[rc(val ?? 0)];
      return `
        <div class="score-card">
          <div class="score-label">${label}</div>
          <div class="score-num" style="color:${col}">${val != null ? (val * 100).toFixed(1) + '%' : '—'}</div>
          <div class="score-sub">${sub}</div>
          <div class="bar"><div class="bar-fill" style="width:${(val ?? 0) * 100}%;background:${col}"></div></div>
        </div>`;
    }).join('')}
  </div>

  <div class="shap-section">
    <div class="section-label">Factors determinants · SHAP</div>
    ${[...p.shap]
      .sort((a, b) => Math.abs(b.shap) - Math.abs(a.shap))
      .slice(0, 6)
      .map(f => {
        const maxAbs = Math.max(...p.shap.map(x => Math.abs(x.shap)));
        const w = Math.abs(f.shap) / maxAbs * 100;
        const pos = f.shap > 0;
        return `
          <div class="shap-row">
            <span class="shap-name">${f.name}</span>
            <span class="shap-val">${f.value}</span>
            <div class="shap-track">
              <div class="${pos ? 'shap-fill-pos' : 'shap-fill-neg'}" style="width:${w}%"></div>
            </div>
            <span class="shap-delta" style="color:${pos ? '#c0392b' : '#27ae60'}">
              ${pos ? '+' : ''}${f.shap.toFixed(3)}
            </span>
          </div>`;
      }).join('')}
  </div>

  <div class="section-label">Resum clínic</div>
  <div class="report-text">${text}</div>

  <div class="disclaimer">
    ⚠ Informe generat per GPT-4o amb caràcter orientatiu.
    No substitueix el judici clínic del professional responsable ni constitueix un document mèdic oficial.
  </div>
  <div class="footer">
    <span>HACKATÓ URV · HOSPITAL JOAN XXIII · ICS ATENCIÓ PRIMÀRIA · GiPSS</span>
    <span>Pacient #${p.id} · ${timestamp}</span>
  </div>

  <script>
    window.addEventListener('load', () => {
      setTimeout(() => { window.print(); }, 400);
    });
  <\/script>
</body>
</html>`);
  win.document.close();
}

const cronicColor = {
  'NO':   { bg: '#f0faf4', color: '#27ae60' },  // green
  'PCC':  { bg: '#fef9f0', color: '#e67e22' },  // orange
  'MACA': { bg: '#fdf3f2', color: '#c0392b' },  // red
};

/* ── Animated score number ───────────────── */
function ScoreNum({ value, cls }) {
  return (
    <div className={`score-card__num score-card__num--${cls}`}>
      {value != null ? `${(value * 100).toFixed(1)}%` : '—'}
    </div>
  );
}

/* ── Score card ──────────────────────────── */
function ScoreCard({ label, value, sublabel, model }) {
  if (value == null) return (
    <div className="score-card">
      <div className="score-card__top">
        <div className="score-card__label">{label}</div>
      </div>
      <div className="score-card__na">No aplicable per a aquest pacient</div>
      <div className="score-card__sub">{sublabel}</div>
      <div className="score-card__model">{model}</div>
    </div>
  );
  const r = riskOf(value);
  const badgeColors = { red: { bg: '#fdf3f2', color: '#c0392b' }, orange: { bg: '#fef9f0', color: '#e67e22' }, green: { bg: '#f0faf4', color: '#27ae60' } };
  return (
    <div className="score-card">
      <div className="score-card__top">
        <div className="score-card__label">{label}</div>
        <span className="score-card__badge" style={{ background: badgeColors[r].bg, color: badgeColors[r].color }}>
          {riskLabel(value)}
        </span>
      </div>
      <ScoreNum value={value} cls={r} />
      <div className="score-card__bar">
        <div className={`score-card__fill fill--${r}`} style={{ width: `${value * 100}%` }} />
      </div>
      <div className="score-card__sub">{sublabel}</div>
      <div className="score-card__model">{model}</div>
    </div>
  );
}

/* ── SHAP row ────────────────────────────── */
function ShapRow({ feat, maxAbs }) {
  const pctW = Math.abs(feat.shap) / maxAbs * 100;
  const pos  = feat.shap > 0;
  return (
    <div className="shap-row">
      <span className="shap-name">{feat.name}</span>
      <span className="shap-val">{feat.value}</span>
      <div className="shap-track">
        <div
          className={`shap-fill--${pos ? 'pos' : 'neg'}`}
          style={{ width: `${pctW}%` }}
        />
      </div>
      <span className={`shap-delta shap-delta--${pos ? 'pos' : 'neg'}`}>
        {pos ? '+' : ''}{feat.shap.toFixed(3)}
      </span>
    </div>
  );
}

/* ── Patient search ──────────────────────── */
function PatientSearch({ onSelect, onPatientFound }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);

    if (query.trim() === '') {
      setResults([]);
      setNotFound(false);
      setError(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);

      try {
        const API = process.env.REACT_APP_API_URL || 'https://hackato-urv-ai.onrender.com';
        const res = await fetch(`${API}/task1/${encodeURIComponent(query.trim())}`);

        if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);

        const data = await res.json();
        console.log(data);

        if (!data || data.prob_mort == null) {
          setResults([]);
          setNotFound(true);
        } else {
          const enriched = {
            id: data.id,
            prob_mort: data.prob_mort,
            prob_urgencia: data.prob_urgencia,
            prob_pcc: null,
            grup_edat: data.grup_edat,
            sexe: data.sexe,
            cronic: data.cronic,
            diags: data.diags, farmacs: data.farmacs, urgencies: data.urgencies, ingressos: data.ingressos,
            nearest_pcc: data.nearest_pcc,
            shap: data.shap,
          };
          setResults([enriched]);
          onPatientFound(enriched);
          onSelect(enriched.id);
          setNotFound(false);
        }
      } catch (err) {
        setError(err.message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = (id) => {
    setQuery('');
    setResults([]);
    onSelect(id);
  };

  return (
    <div className="search-box">
      <input
        className="search-input"
        type="text"
        placeholder="Introdueix ID pacient..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {loading && (
        <div className="search-not-found">Cercant...</div>
      )}

      {error && (
        <div className="search-not-found" style={{ color: 'var(--red)' }}>
          Error: {error}
        </div>
      )}

      {results.length > 0 && (
        <div className="search-results">
          {results.map(p => (
            <button key={p.id} className="search-result-item" onClick={() => handleSelect(p.id)}>
              <span className="search-result-id">#{p.id}</span>
              <span className="search-result-meta">{p.grup_edat} · {p.cronic}</span>
            </button>
          ))}
        </div>
      )}

      {!loading && notFound && (
        <div className="search-not-found">Cap pacient trobat amb ID "{query}"</div>
      )}
    </div>
  );
}


/* ── Main ────────────────────────────────── */
export default function App() {
  const [selId, setSelId] = useState(PATIENTS[0].id);
  const [tab, setTab]     = useState('overview');
  const [apiPatient, setApiPatient] = useState(null);
  const [informeLoading, setInformeLoading] = useState(false);
  const [informeError,   setInformeError]   = useState('');

  const p = (apiPatient && String(apiPatient.id) === String(selId) ? apiPatient : null) ?? PATIENTS.find(x => x.id === selId) ?? apiPatient;
  const r   = riskOf(p.prob_mort);
  const combined = (p.prob_mort * 0.7 + (p.prob_urgencia ?? 0) * 0.3);
  const rC  = riskOf(combined);
  const maxShap = Math.max(...p.shap.map(f => Math.abs(f.shap)));

  // Reset error when patient changes
  useEffect(() => { setInformeError(''); }, [selId]);

  const handleGenerarInforme = useCallback(async () => {
    setInformeLoading(true);
    setInformeError('');
    try {
      const { system, user } = buildPrompt(p, combined);

      // En producció substitueix per: fetch('/api/informe', { method:'POST', body: JSON.stringify({system, user}) })
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${OPENAI_KEY}` },
        body: JSON.stringify({
          model: 'gpt-4o',
          temperature: 0.25,
          max_tokens: 600,
          messages: [
            { role: 'system', content: system },
            { role: 'user',   content: user   },
          ],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message ?? `OpenAI error ${res.status}`);
      }
      const data    = await res.json();
      const text    = data.choices[0].message.content.trim();
      const ts      = new Date().toLocaleString('ca-ES', { dateStyle: 'short', timeStyle: 'short' });
      descarregarPdf(p, combined, text, ts);
    } catch (e) {
      setInformeError(e.message);
    } finally {
      setInformeLoading(false);
    }
  }, [p, combined]);

  const alertText = {
    red:    'Revisió prioritària recomanada',
    orange: 'Seguiment intensificat recomanat',
    green:  'Seguiment estàndard',
  };
  const alertIcon = { red: '🔴', orange: '🟡', green: '🟢' };

  return (
    <>
      {/* ── HEADER ── */}
      <header className="header">
        <div className="header__brand">
          <div className="header__cross">+</div>
          <div>
            <div className="header__name">TarraCare - XXIII · Sistema d'Alerta Clínica</div>
            <div className="header__sub">Institut Català de la Salut · Hospital Joan XXIII · Hackató URV 2025</div>
          </div>
        </div>
        <nav className="header__nav">
          <button className={`header__nav-item ${tab === 'overview' ? 'header__nav-item--active' : ''}`} onClick={() => setTab('overview')}>Visió general</button>
          <button className={`header__nav-item ${tab === 'shap' ? 'header__nav-item--active' : ''}`} onClick={() => setTab('shap')}>Explicabilitat</button>
          <button className={`header__nav-item ${tab === 'profile' ? 'header__nav-item--active' : ''}`} onClick={() => setTab('profile')}>Perfil clínic</button>
        </nav>
        <div className="header__right">
          <div className={`header__badge header__badge--${rC}`}>
            <span className="badge-dot" />
            Risc {riskLabel(combined)}
          </div>
        </div>
      </header>

      <div className="layout">
        {/* ── SIDEBAR ── */}
        <aside className="sidebar">
          <div className="sidebar__label">Cerca de pacient</div>
          <PatientSearch
            onSelect={(id) => { setSelId(id); setTab('overview'); }}
            onPatientFound={setApiPatient}
          />

          <div className="sidebar__divider" />
          {[
            
          ].map(({ name, m }) => (
            <div key={name} className="metric-block">
              <div className="metric-block__name">{name}</div>
              <div className="metric-row"><span>AUC-ROC</span><span className="metric-row__val">{m.auc.toFixed(3)}</span></div>
              <div className="metric-row"><span>Recall</span><span className="metric-row__val">{m.recall.toFixed(3)}</span></div>
              <div className="metric-row"><span>Precisió</span><span className="metric-row__val">{m.precision.toFixed(3)}</span></div>
            </div>
          ))}
        </aside>

        {/* ── MAIN ── */}
        <main className="main">
          {/* Patient header */}
          <div className="page-header">
            <div className="page-header__left">
              <div className="page-header__label">Pacient seleccionat</div>
              <div className="page-header__title">
                Pacient #{p.id}
              </div>
              <div className="page-header__sub">
                {p.cronic === 'NO' && p.prob_pcc > 0.6 &&
                  <span style={{ marginLeft: 10, background: '#e8f3fb', color: '#0071bc', fontWeight: 700, fontSize: 12, padding: '2px 10px', borderRadius: 20, border: '1px solid #cce2f5' }}>
                    TarraCare XXIII
                  </span>
                }
              </div>
              {/* ── Botó informe ── */}
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button
                  className="informe-btn"
                  onClick={handleGenerarInforme}
                  disabled={informeLoading}
                >
                  {informeLoading
                    ? <><span className="informe-spinner" /> Generant informe…</>
                    : '⬇ Generar i descarregar informe'
                  }
                </button>
                {informeError && (
                  <span className="informe-error">{informeError}</span>
                )}
              </div>
            </div>
            <div className="page-header__score">
              <div className="score-label">Score integrat</div>
              <div className={`score-big score-big--${rC}`}>{(combined * 100).toFixed(1)}%</div>
              <div className={`header__badge header__badge--${rC}`} style={{ fontSize: 11 }}>
                Risc {riskLabel(combined)}
              </div>
            </div>
          </div>

          {/* Alert banner */}
          <div className={`alert-banner alert-banner--${rC}`}>
            <div className="alert-banner__icon">{alertIcon[rC]}</div>
            <div className="alert-banner__body" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <div className="alert-banner__title">{alertText[rC]}</div>
            </div>
            <div className="alert-banner__pills">
              <div className="pill">
                <span className="pill__label">Mort</span>
                <span className={`pill__val--${riskOf(p.prob_mort)}`}>{pct(p.prob_mort)}</span>
              </div>
              <div className="pill">
                <span className="pill__label">Urgència</span>
                <span className={`pill__val--${riskOf(p.prob_urgencia)}`}>{pct(p.prob_urgencia)}</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            {[['overview','Visió general'], ['shap','Explicabilitat SHAP'], ['profile','Perfil clínic']].map(([k, v]) => (
              <button key={k} className={`tab-btn ${tab === k ? 'tab-btn--active' : ''}`} onClick={() => setTab(k)}>{v}</button>
            ))}
          </div>

          {/* ── OVERVIEW ── */}
          {tab === 'overview' && (
            <>
              <div className="scores-row">
                <ScoreCard label="Risc de mortalitat · 1 any" value={p.prob_mort} sublabel="Probabilitat de mort en els propers 12 mesos" />
                <ScoreCard label="Urgències · proper mes" value={p.prob_urgencia} sublabel="Probabilitat de visita a urgències en 30 dies" />
                <div className="score-card">
                <div className="score-card__top">
                  <div className="score-card__label">Grup DE Cronicitat</div>
                </div>
                <div className="score-card__num" style={{ fontSize: 32, color: cronicColor[p.cronic]?.color ?? 'var(--gray-800)' }}>
                  {p.cronic}
                </div>
                <div className="score-card__sub">Grup de cronicitat assignat al pacient</div>
              </div>
              </div>
              <div className="section-title">Resum d'activitat clínica</div>
              <div className="stats-row">
                {[
                  ['Diagnòstics totals', p.diags],
                  ['Fàrmacs actius', p.farmacs],
                  ['Urgències (any)', p.urgencies],
                  ['Ingressos (any)', p.ingressos],
                ].map(([label, val]) => (
                  <div key={label} className="stat">
                    <div className="stat__num">{val}</div>
                    <div className="stat__label">{label}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── SHAP ── */}
          {tab === 'shap' && (
            <div className="shap-panel">
              <div className="section-title">Explicabilitat SHAP — model de mortalitat</div>
              <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 16, lineHeight: 1.6 }}>
                Cada barra mostra la contribució d'una variable a la predicció. Les barres <strong style={{ color: 'var(--red)' }}>vermelles</strong> incrementen el risc; les <strong style={{ color: 'var(--green)' }}>verdes</strong> el redueixen.
              </p>
              <div className="shap-legend">
                <span className="shap-legend__item"><span className="dot-red" /> Incrementa el risc de mortalitat</span>
                <span className="shap-legend__item"><span className="dot-green" /> Redueix el risc de mortalitat</span>
              </div>
              {[...p.shap].sort((a, b) => Math.abs(b.shap) - Math.abs(a.shap)).map(f => (
                <ShapRow key={f.name} feat={f} maxAbs={maxShap} />
              ))}
              <div className="shap-top3">
                <div className="shap-top3__title">Les 3 variables més influents</div>
                <div className="shap-top3__pills">
                  {[...p.shap].sort((a, b) => Math.abs(b.shap) - Math.abs(a.shap)).slice(0, 3).map(f => (
                    <div key={f.name} className={`shap-pill shap-pill--${f.shap > 0 ? 'pos' : 'neg'}`}>
                      {f.shap > 0 ? '↑ ' : '↓ '} <strong>{f.name}</strong> = {f.value}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── PROFILE ── */}
          {tab === 'profile' && (
            <div className="profile-grid">
              <div className="profile-card">
                <div className="profile-card__title">Dades demogràfiques</div>
                {[
                  ['ID Pacient', `#${p.id}`],
                  ['Grup d\'edat', p.grup_edat],
                  ['Sexe', p.sexe === 'H' ? 'Home' : 'Dona'],
                  ['Cronicitat', p.cronic],
                ].map(([k, v]) => (
                  <div key={k} className="profile-row">
                    <span className="profile-row__key">{k}</span>
                    <span className="profile-row__val">{v}</span>
                  </div>
                ))}
              </div>

              <div className="profile-card">
                <div className="profile-card__title">Activitat clínica</div>
                {[
                  ['Diagnòstics totals', p.diags],
                  ['Fàrmacs actius', p.farmacs],
                  ['Urgències (any)', p.urgencies],
                  ['Ingressos (any)', p.ingressos],
                ].map(([k, v]) => (
                  <div key={k} className="profile-row">
                    <span className="profile-row__key">{k}</span>
                    <span className="profile-row__val">{v}</span>
                  </div>
                ))}
              </div>

              <div className="profile-card">
                <div className="profile-card__title">Scores de risc</div>
                {[
                  ['Mortalitat 1 any', pct(p.prob_mort), riskOf(p.prob_mort),'blue'],
                  ['Urgència proper mes', pct(p.prob_urgencia), riskOf(p.prob_urgencia)],
                  ['Score integrat', pct(combined), rC],
                ].map(([k, v, col]) => (
                  <div key={k} className="profile-row">
                    <span className="profile-row__key">{k}</span>
                    <span className={`profile-row__val profile-row__val--${col}`}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer__left">
          <div className="footer__logo">+</div>
          <span>TarraCare - XXIII · Sistema d'Alerta Clínica · Hackató URV 2025</span>
        </div>
        <div className="footer__right">"No prediem mortalitat — evitem que el metge perdi un pacient d'alt risc"</div>
      </footer>
    </>
  );
}